import ProTable, {ProColumns} from '@ant-design/pro-table';
import axiosApiInstance from '../../../api/axiosClient';
import {
    ArrowRightOutlined,
    ClockCircleOutlined,
    DownloadOutlined,
    EditOutlined,
    FolderOutlined,
    LeftOutlined,
    PlusOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    Badge,
    Button,
    Checkbox,
    Col,
    Collapse,
    Form,
    FormInstance,
    Image,
    message,
    Modal,
    notification,
    Pagination,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    TableColumnsType,
    Tooltip
} from 'antd';
import {DDEXPageContext} from './DDEXMainPage';
import {atom, useAtom, useAtomValue} from 'jotai';
import {useContext, useEffect, useRef, useState} from 'react';
import {groupIdentifiersTotalAtom, releasesViewAtom, selectedGroupReleasesTotalAtom} from './ddex_atoms';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import ExportReleasesExcel from './ExportReleasesExcel';
import ImportUpdateReleasesExcel from './ImportUpdateReleasesExcel';
import dayjs from 'dayjs';
import ProCard from '@ant-design/pro-card';
import UploadRelease from './UploadRelease';
import ModifyRelease from './ModifyRelease';
import TakedownRelease from './TakedownRelease';
import ExportTemplateExcel from './ExportTemplateExcel';
import {CheckboxValueType} from 'antd/es/checkbox/Group';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpotify, faYoutube} from '@fortawesome/free-brands-svg-icons';
import {faB} from '@fortawesome/free-solid-svg-icons';
import DownloadBinariesModal from './DownloadAllModal';
import {fetchReleaseFiles, UploadedFile} from './fileUtils';
import {useQuery} from '@tanstack/react-query';
import {atomWithQuery} from 'jotai-tanstack-query';
import {searchCriteriaAtom} from './SearchForm';
import {latestDeliveryForPlatform, originalUploadBlockedStatus} from './deliveryStatus';
import {SortOrder} from 'antd/es/table/interface';
import {API} from "../../../common/constants"

type archiveStatusType = { [k: string]: Array<CheckboxValueType> }
// type archiveStatusType = { [k: string]:   "archived" | "unarchived"}
type upcToPlatformMapType = { [k: string]: Array<CheckboxValueType> }
type upcToFormRefMapType = { [k: string]: FormInstance<any> }
type selectedTotalsType = {
    youtube: number
    believe: number
    spotify: number
    total: number
}

const archiveStatusAtom = atom<archiveStatusType>({})
const upcToPlatformMapAtom = atom<upcToPlatformMapType>({})
const upcToFormRefMapAtom = atom<upcToFormRefMapType>({})
const selectedTotalsAtom = atom<selectedTotalsType>({
    youtube: 0,
    believe: 0,
    spotify: 0,
    total: 0,
})

interface ArtistIdentifierAPI {
    artist_name: string;
    number_of_releases: number;
    releases_list: string;
}

interface WeekIdentifierAPI {
    week_range_mon_sun: string;
    week_start_date: string;
    number_of_releases: number;
}

type GroupIdentifierData = ArtistIdentifierAPI | WeekIdentifierAPI;

interface GroupByApiResponse {
    groups: GroupIdentifierData[];
    total_groups: number;
}

interface ReleaseGroupArtist {
    artist_name: string;
    releases: API.MusicDistribution.MusicRelease[];
    releasesCurrentPage?: number;
    releasesPageSize?: number;
    releasesLoading?: boolean;
}

interface ReleaseGroupWeek {
    week_range_mon_sun: string;
    week_start_date: string;
    releases: API.MusicDistribution.MusicRelease[];
    releasesCurrentPage?: number;
    releasesPageSize?: number;
    releasesLoading?: boolean;
}

type GroupedReleaseData = ReleaseGroupArtist | ReleaseGroupWeek;

interface APIArtistReleasesResponseAPI {
    artist_name: string;
    releases: API.MusicDistribution.MusicRelease[];
    total_releases: number;
}

interface APIWeekReleasesResponseAPI {
    week_range_mon_sun: string;
    releases: API.MusicDistribution.MusicRelease[];
    total_releases: number;
}


const GROUP_OPTIONS = [
    {label: "None", value: "none"},
    {label: "By Date (Week)", value: "week"},
    {label: "By Artist", value: "artist"},
]
const ORDER_OPTIONS = [
    {label: "Newest First", value: "desc"},
    {label: "Oldest First", value: "asc"},
]
const FOLDER_PER_PAGE = 10;
const FOLDER_VIEW_PAGE_SIZE = 50;

const {Panel} = Collapse;


const getSelectedUPCs = (upcMap: upcToPlatformMapType) =>
    Object.keys(upcMap).filter((k) => upcMap[k].length > 0)

const UploadStatusBadgeMap = {
    uploaded: (
        <Badge
            text="uploaded"
            color="green"
        />
    ),
    modified: (
        <Badge
            text="modified"
            color="blue"
        />
    ),
    takedown: (
        <Badge
            text="takedown"
            color="red"
        />
    ),
}

// DeliveryStatusBadgeMap renders the reconciled per-platform ingestion status.
// "sent" = delivered, awaiting the distributor's acknowledgement; "ingested" =
// confirmed live; "failed" = the distributor reported an error (click to see it);
// "stale" = no acknowledgement within the SLA window.
const DeliveryStatusBadgeMap: Record<string, JSX.Element> = {
    pending: <Badge status="warning" text="pending" />,
    sent: <Badge color="blue" text="sent" />,
    ingested: <Badge color="green" text="ingested" />,
    failed: <Badge color="red" text="failed" />,
    stale: <Badge color="orange" text="stale" />,
}

// renderPlatformDeliveryCell shows the reconciled Delivery status for a platform,
// falling back to the legacy upload log for releases delivered before delivery
// reconciliation existed. A failed/stale delivery is clickable and opens the
// stored distributor message.
function renderPlatformDeliveryCell(record: API.MusicDistribution.MusicRelease, platform: string) {
    const d = latestDeliveryForPlatform(record, platform)
    if (d) {
        const badge = DeliveryStatusBadgeMap[d.status] ?? <Badge status="default" text={d.status} />
        const inner = (
            <Tooltip title={dayjs(d.sent_at).format('YYYY-MM-DD HH:mm')}>{badge}</Tooltip>
        )
        const isProblem = d.status === 'failed' || d.status === 'stale'
        if (isProblem && d.message) {
            return (
                <a
                    onClick={() =>
                        Modal.info({
                            title: `${platform} delivery — ${d.status}`,
                            width: 800,
                            content: (
                                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto' }}>
                                    {d.message}
                                </pre>
                            ),
                        })
                    }
                >
                    {inner}
                </a>
            )
        }
        return inner
    }
    // Fallback: no reconciled delivery yet — show the legacy upload log if present.
    const log = record.release_upload_status_logs?.find((l) => l.platform === platform)
    return log ? (
        <Tooltip title={dayjs(log.created_at).format('YYYY-MM-DD HH:mm')}>
            {UploadStatusBadgeMap[log.status]}
        </Tooltip>
    ) : (
        <Badge status="warning" text="pending" />
    )
}

const ArchiveStatusBadgeMap = {
    archived: (
        <Badge
            text="Archived"
            color="green"
        />
    ),
    unarchived: (
        <Badge
            text="Not Archived"
            color="red"
        />
    ),
}

const CHECKBOX_PADDING = "5px"

const CHECKBOX_COL_STYLE = {
    padding: `0px ${CHECKBOX_PADDING}`,
}

type UploaderProps = {
    entity: API.MusicDistribution.MusicRelease
}

type CheckAllProps = {
    selectAllFunc: (platform: string, checked: boolean) => void
}

type AlbumCoverProps = {
    upc: string
    filename: string
    id: string
}

const releaseTotalsAtom = atomWithQuery((get) => {
    const searchParams = get(searchCriteriaAtom)
    return {
        staleTime: 2 * 60 * 1000, // 2 minutes
        queryKey: ["totals", "releases", searchParams],
        queryFn: () =>
            axiosApiInstance
                .get<number>("/api/products/totals/releases", {
                    params: {
                        ...searchParams,
                        is_pending: false,
                    },
                })
                .then(({data}) => data),
    }
})

export const releasesLayoutAtom = atom<"table" | "folders">("table");

export const releaseDataAtom = atomWithQuery((get) => {
    const searchParams = get(searchCriteriaAtom)
    const summary = get(releasesLayoutAtom) === "folders"
    const releaseTotals = get(releaseTotalsAtom)
    return {
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Folder view only needs id, upc, and title, so it does not wait on the count.
        enabled: summary || releaseTotals.isFetched,
        queryKey: ["releases", searchParams, summary ? "summary" : "full"],
        queryFn: () =>
            axiosApiInstance
                .get<API.MusicDistribution.MusicRelease[]>("/api/products/releases/", {
                    params: {
                        ...searchParams,
                        is_pending: false,
                        ...(summary ? {summary: 1} : {}),
                    },
                })
                .then(({data}) => data),
    }
})

export const AlbumCover = ({upc, filename, id}: AlbumCoverProps) => {
    // const [imageUrl, setImageUrl] = useState('blank');
    const {data: imageUrl} = useQuery({
        refetchInterval: 3 * 60 * 1000, // 3 minutes
        staleTime: 3 * 60 * 1000, // 3 minutes
        queryKey: ["album_cover", id, filename],
        queryFn: ({queryKey}) => {
            return axiosApiInstance
                .post<{ presignedUrl: string }>(
                    "/api/products/releases/generate-presigned-url-get",
                    {
                        fileName: filename,
                        folderName: id, // Use ID instead of UPC for folder name
                    }
                )
                .then(({data}) => data.presignedUrl)
                .catch(() => "blank")
        },
    })

    return (
        // <div style={{ boxShadow: '10px 10px #555'}}>
        <Image
            wrapperStyle={{
                boxShadow: "0 0 5px #555",
            }}
            width={80}
            height={80}
            src={imageUrl}
            placeholder
        />
        // </div>
    )
}

// type UploadRowDataType = {
//     key: string;
//     upc: string;
//     title: string;
//     selected: {
//         youtube: boolean;
//         believe: boolean;
//         spotify: boolean;
//     };
// };

const CheckAllCheckbox = ({selectAllFunc}: CheckAllProps) => {
    const [selectedTotals] = useAtom(selectedTotalsAtom)
    const releases = useAtomValue(releaseDataAtom)

    return (
        <Checkbox.Group>
            <Row style={{width: 60}}>
                <Col
                    span={8}
                    style={CHECKBOX_COL_STYLE}
                >
                    <Checkbox
                        value={"youtube"}
                        key={"checkbox-all-youtube"}
                        indeterminate={
                            releases.data?.length > selectedTotals.youtube &&
                            selectedTotals.youtube > 0
                        }
                        onChange={(e) => {
                            const checked = e.target.checked
                            selectAllFunc("youtube", checked)
                        }}
                        checked={releases.data?.length == selectedTotals.youtube}
                    />
                </Col>
                <Col
                    span={8}
                    style={CHECKBOX_COL_STYLE}
                >
                    <Checkbox
                        value={"believe"}
                        key={"checkbox-all-believe"}
                        indeterminate={
                            releases.data?.length > selectedTotals.believe &&
                            selectedTotals.believe > 0
                        }
                        onChange={(e) => {
                            const checked = e.target.checked
                            selectAllFunc("believe", checked)
                        }}
                        checked={releases.data?.length == selectedTotals.believe}
                    />
                </Col>
                <Col
                    span={8}
                    style={CHECKBOX_COL_STYLE}
                >
                    <Checkbox
                        value={"spotify"}
                        key={"checkbox-all-spotify"}
                        indeterminate={
                            releases.data?.length > selectedTotals.spotify &&
                            selectedTotals.spotify > 0
                        }
                        onChange={(e) => {
                            const checked = e.target.checked
                            selectAllFunc("spotify", checked)
                        }}
                        checked={releases.data?.length == selectedTotals.spotify}
                    />
                </Col>
            </Row>
        </Checkbox.Group>
    )
}

const UploaderCheckbox = ({entity}: UploaderProps) => {
    const [, setUpcToPlatformMap] = useAtom(upcToPlatformMapAtom)
    const [, setUpcToFormRefMap] = useAtom(upcToFormRefMapAtom)
    const [, setSelectedTotals] = useAtom(selectedTotalsAtom)
    const [form] = Form.useForm()

    useEffect(() => {
        setUpcToFormRefMap((m) => {
            m[entity.upc_ean] = form
            return m
        })
    }, [entity.upc_ean, setUpcToFormRefMap, form])

    return (
        <Form
            form={form}
            style={{height: 32, maxHeight: 32, marginTop: 4}}
        >
            <Form.Item name={`${entity.upc_ean}-checkbox-group`}>
                <Checkbox.Group
                    onChange={(value) => {
                        const id = entity.upc_ean
                        setUpcToPlatformMap((sel) => {
                            sel[id] = value
                            return sel
                        })
                    }}
                >
                    <Row style={{width: 60}}>
                        {["youtube", "believe", "spotify"].map((platform) => (
                            <Col
                                key={`${entity.upc_ean}-${platform}-checkbox-column`}
                                span={8}
                                style={CHECKBOX_COL_STYLE}
                            >
                                <Checkbox
                                    value={platform}
                                    onChange={(e) => {
                                        setSelectedTotals((prev) => {
                                            if (e.target === undefined) {
                                                return prev
                                            }

                                            const checked = e.target.checked
                                            const newVals = {...prev}

                                            if (checked) {
                                                newVals[platform] += 1
                                                newVals.total += 1
                                            } else {
                                                newVals[platform] -= 1
                                                newVals.total -= 1
                                            }

                                            return newVals
                                        })
                                    }}
                                />
                            </Col>
                        ))}
                    </Row>
                </Checkbox.Group>
            </Form.Item>
        </Form>
    )
}

const formatFileSize = (size: number) => {
    const kb = size / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(2)} KB`;
    }
    return `${(kb / 1024).toFixed(2)} MB`;
};

const formatFileDuration = (seconds?: number) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const downloadReleaseFile = async (folderName: string, fileName: string) => {
    const {data} = await axiosApiInstance.post<{ presignedUrl: string }>(
        "/api/products/releases/generate-presigned-url-get",
        {fileName, folderName, download: true},
    );
    const link = document.createElement("a");
    link.href = data.presignedUrl;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const ReleaseFolderFiles = ({
    release,
    onBack,
}: {
    release: API.MusicDistribution.MusicRelease;
    onBack: () => void;
}) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchReleaseFiles(release.id, (next) => {
            if (!cancelled) setFiles(next);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [release.id]);

    const onDownload = async (file: UploadedFile) => {
        setDownloading(file.uid);
        try {
            await downloadReleaseFile(release.id, file.name);
        } catch (error) {
            console.error(error);
            message.error(`Failed to download ${file.name}`);
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div style={{padding: 16}}>
            <Button onClick={onBack} icon={<LeftOutlined/>} style={{marginBottom: 16}}>
                Back to folders
            </Button>
            <div style={{marginBottom: 16}}>
                <div style={{fontSize: 18, fontWeight: 600}}>{release.upc_ean}</div>
                {release.name ? <div style={{color: "#888"}}>{release.name}</div> : null}
            </div>
            <Table<UploadedFile>
                rowKey="uid"
                loading={loading}
                dataSource={files}
                pagination={false}
                locale={{emptyText: loading ? "Loading files..." : "No uploaded files"}}
                columns={[
                    {
                        title: "File",
                        dataIndex: "name",
                        render: (name: string, file) => (
                            <Space>
                                {file.fileType === "image" && file.url ? (
                                    <Image src={file.url} width={36} height={36} style={{objectFit: "cover"}}/>
                                ) : null}
                                <span>{name}</span>
                            </Space>
                        ),
                    },
                    {
                        title: "Type",
                        dataIndex: "fileType",
                        width: 110,
                        render: (type: string) => {
                            const color = type === "image" ? "blue" : type === "audio" ? "green" : "default";
                            return <Tag color={color}>{(type || "other").toUpperCase()}</Tag>;
                        },
                    },
                    {
                        title: "Size",
                        dataIndex: "size",
                        width: 120,
                        render: (size: number) => formatFileSize(size || 0),
                    },
                    {
                        title: "Duration",
                        dataIndex: "duration",
                        width: 110,
                        render: (duration: number, file) => file.fileType === "audio" ? formatFileDuration(duration) : "-",
                    },
                    {
                        title: "",
                        width: 130,
                        render: (_value, file) => (
                            <Button
                                type="link"
                                icon={<DownloadOutlined/>}
                                loading={downloading === file.uid}
                                onClick={() => onDownload(file)}
                            >
                                Download
                            </Button>
                        ),
                    },
                ]}
            />
        </div>
    );
};

const ReleaseFolderGrid = ({
    releases,
    loading,
    onOpen,
}: {
    releases: API.MusicDistribution.MusicRelease[];
    loading: boolean;
    onOpen: (release: API.MusicDistribution.MusicRelease) => void;
}) => {
    if (loading) {
        return (
            <div style={{textAlign: "center", padding: 48}}>
                <Spin/>
            </div>
        );
    }
    if (releases.length === 0) {
        return (
            <div style={{textAlign: "center", padding: 48, color: "#aaa"}}>
                No releases
            </div>
        );
    }
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: 4,
                padding: 16,
            }}
        >
            {releases.map((release) => (
                <Tooltip key={release.id || release.upc_ean} title={release.name}>
                    <Button
                        type="text"
                        onClick={() => onOpen(release)}
                        style={{
                            height: "auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                            padding: "12px 8px",
                        }}
                    >
                        <FolderOutlined style={{fontSize: 40, color: "#e8b931"}}/>
                        <span style={{fontSize: 12, lineHeight: 1.3, wordBreak: "break-all"}}>
                            {release.upc_ean}
                        </span>
                    </Button>
                </Tooltip>
            ))}
        </div>
    );
};

const ReleasesTable = () => {
    const [groupBy, setGroupBy] = useState<"none" | "artist" | "week">("none");
    const [layout, setLayout] = useAtom(releasesLayoutAtom);
    const [openFolder, setOpenFolder] = useState<API.MusicDistribution.MusicRelease | null>(null);
    const tablePageSizeRef = useRef(10);
    const [order, setOrder] = useState<"desc" | "asc">("desc");
    const [groupIdentifiers, setGroupIdentifiers] = useState<GroupIdentifierData[]>([]);
    const [loadingGroupIdentifiers, setLoadingGroupIdentifiers] = useState(false);
    const [currentGroupIdentifiersPage, setCurrentGroupIdentifiersPage] = useState(1);
    const [totalGroupIdentifiers, setTotalGroupIdentifiers] = useState(0);
    const [totalGroupIdentifiersState, setTotalGroupIdentifiersState] = useAtom(groupIdentifiersTotalAtom);
    const totalReleasesForSelectedGroupAtom = useAtomValue(selectedGroupReleasesTotalAtom);
    const [selectedAndLoadedGroup, setSelectedAndLoadedGroup] = useState<ReleaseGroupArtist | ReleaseGroupWeek | null>(null);
    const [loadingSelectedGroupReleases, setLoadingSelectedGroupReleases] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<GroupedReleaseData | null>(null);
    const [archiveStatuses, setArchiveStatuses] = useState<{ [key: string]: boolean | null }>({});
    const [upcToPlatformMap, setUpcToPlatformMap] = useAtom(upcToPlatformMapAtom)
    const [upcToFormRefMap] = useAtom(upcToFormRefMapAtom)
    const [searchCriteria, setSearchCriteria] = useAtom(searchCriteriaAtom)
    const [selectedTotals, setSelectedTotals] = useAtom(selectedTotalsAtom)
    const {releasesActionRef} = useContext(DDEXPageContext)
    const [releasesView] = useAtom(releasesViewAtom)
    const [upcToNameMap, setUpcToNameMap] = useState<{ [k: string]: string }>({})
    const [isOpen, setIsOpen] = useState(false)
    const releases = useAtomValue(releaseDataAtom)
    const {data: totalReleasesForNoneView} = useAtomValue(releaseTotalsAtom);
    const releasesForNoneView = useAtomValue(releaseDataAtom)
    const [activeGroupIdentifier, setActiveGroupIdentifier] = useState<GroupIdentifierData | null>(null);

    useEffect(() => {
        if (layout !== "folders") return;
        if ((searchCriteria.pageSize ?? 10) >= FOLDER_VIEW_PAGE_SIZE) return;
        setSearchCriteria((prev) => ({...prev, pageSize: FOLDER_VIEW_PAGE_SIZE}));
    }, [layout, searchCriteria.pageSize, setSearchCriteria]);


    useEffect(() => {
        if (groupBy === "none") {
            setGroupIdentifiers([]);
            setSelectedAndLoadedGroup(null);
            setTotalGroupIdentifiersState(0);
            return;
        }
        setLoadingGroupIdentifiers(true);
        const params: any = {
            groupBy: groupBy,
            page: currentGroupIdentifiersPage,
            pageSize: FOLDER_PER_PAGE,
        };
        if (groupBy === "week") {
            params.order = order;
        }
        console.log("[GROUP IDENTIFIERS] Fetching (body mode) with params:", JSON.stringify(params));
        axiosApiInstance
            .get<GroupByApiResponse>("/api/products/release_groups/groupBy", {params})
            .then(({data}) => {
                console.log("[GROUP IDENTIFIERS] Raw API Response Data (body mode):", data);
                if (data && typeof data === 'object' && Array.isArray(data.groups) && typeof data.total_groups === 'number') {
                    setGroupIdentifiers(data.groups);
                    setTotalGroupIdentifiersState(data.total_groups);
                    console.log(`[GROUP IDENTIFIERS] Received for ${groupBy}: ${data.groups.length} items. Total from body: ${data.total_groups}`);
                } else {
                    console.error("[GROUP IDENTIFIERS] Invalid API response structure (body mode):", data);
                    message.error("Failed to load group identifiers due to unexpected response.");
                    setGroupIdentifiers([]);
                    setTotalGroupIdentifiersState(0);
                }
            })
            .catch(error => {
                console.error("Error fetching group identifiers (body mode):", error);
                message.error("Failed to load group identifiers.");
                setGroupIdentifiers([]);
                setTotalGroupIdentifiersState(0);
            })
            .finally(() => setLoadingGroupIdentifiers(false));
    }, [groupBy, order, currentGroupIdentifiersPage, setTotalGroupIdentifiersState]);

    const [, setCurrentlySelectedGroupTotal] = useAtom(selectedGroupReleasesTotalAtom);

    const handleBackToGroups = () => {
        setOpenFolder(null);
        setSelectedAndLoadedGroup(null);
        setActiveGroupIdentifier(null);
    }

    const handleGroupIdentifierClick = (group: GroupIdentifierData) => {
        setOpenFolder(null);
        setActiveGroupIdentifier(group)
        fetchReleasesForSelectedGroup(group, 1, layout === "folders" ? FOLDER_VIEW_PAGE_SIZE : FOLDER_PER_PAGE);
    }

    const fetchReleasesForSelectedGroup = async (
        groupIdentifier: GroupIdentifierData,
        releasesPageToLoad: number = 1,
        releasesPageSizeToLoad: number = FOLDER_PER_PAGE,
        summary: boolean = layout === "folders",
    ) => {
        if (!groupIdentifier) return;
        console.log(`[FSRG] Fetching for group. Group ID: ${JSON.stringify(groupIdentifier)}, Page: ${releasesPageToLoad}, PageSize: ${releasesPageSizeToLoad}`);
        setLoadingSelectedGroupReleases(true);
        setSelectedAndLoadedGroup(null);
        const params: any = {
            groupBy: groupBy,
            releasesPage: releasesPageToLoad,
            releasesPageSize: releasesPageSizeToLoad,
            ...(summary ? {summary: 1} : {}),
        };
        let descriptiveIdentifierForLog: string = "Unknown Group";
        if (groupBy === "week") {
            const weekIdent = groupIdentifier as WeekIdentifierAPI;
            if (!weekIdent.week_start_date) {
                console.error("[FSRG] Week identifier is missing week_start_date:", weekIdent);
                setLoadingSelectedGroupReleases(false);
                message.error("Cannot load releases: Missing week identifier data.");
                return;
            }
            params.weekStartDate = weekIdent.week_start_date;
            descriptiveIdentifierForLog = weekIdent.week_start_date;
        } else if (groupBy === "artist") {
            params.artistName = (groupIdentifier as ArtistIdentifierAPI).artist_name;
            descriptiveIdentifierForLog = (groupIdentifier as ArtistIdentifierAPI).artist_name;
        } else {
            setLoadingSelectedGroupReleases(false);
            return;
        }
        console.log(`[FSRG] API Call Params for /releases:`, JSON.stringify(params));
        try {
            const {data: apiResponseData} = await axiosApiInstance.get<APIArtistReleasesResponseAPI | APIWeekReleasesResponseAPI>(
                "/api/products/release_groups/releases", {params}
            );
            if (apiResponseData && typeof apiResponseData === 'object' && 'releases' in apiResponseData && 'total_releases' in apiResponseData) {
                const commonStateForSelectedGroup = {
                    releases: apiResponseData.releases || [],
                    releasesCurrentPage: releasesPageToLoad,
                    releasesPageSize: releasesPageSizeToLoad,
                    releasesLoading: false,
                };
                if (groupBy === "artist" && 'artist_name' in apiResponseData) {
                    setSelectedAndLoadedGroup({
                        artist_name: (groupIdentifier as ArtistIdentifierAPI).artist_name,
                        ...commonStateForSelectedGroup,
                    } as ReleaseGroupArtist);
                } else if (groupBy === "week" && 'week_range_mon_sun' in apiResponseData) {
                    setSelectedAndLoadedGroup({
                        week_range_mon_sun: (groupIdentifier as WeekIdentifierAPI).week_range_mon_sun,
                        week_start_date: (groupIdentifier as WeekIdentifierAPI).week_start_date,
                        ...commonStateForSelectedGroup,
                    } as ReleaseGroupWeek);
                } else {
                    console.error("[FSRG] Mismatch between groupBy and response structure:", groupBy, apiResponseData);
                    throw new Error("API response structure does not match expected type for the group.");
                }
                setCurrentlySelectedGroupTotal(apiResponseData.total_releases);
                console.log(`[FSRG] Success. Total releases from body: ${apiResponseData.total_releases}, Releases count: ${apiResponseData.releases.length}`);
            } else {
                console.error("[FSRG] Invalid or incomplete API response:", apiResponseData);
                setCurrentlySelectedGroupTotal(0);
                setSelectedAndLoadedGroup(prev => prev ? {...prev, releases: [], releasesLoading: false} : null);
            }
        } catch (error) {
            console.error(`[FSRG] Error fetching releases for group ${descriptiveIdentifierForLog}:`, error);
            message.error(`Failed to load releases for group.`);
            setCurrentlySelectedGroupTotal(0);
            const baseErrorState = {
                releases: [],
                releasesCurrentPage: 1,
                releasesPageSize: FOLDER_PER_PAGE,
                releasesLoading: false,
            };
            if (groupBy === "artist") {
                setSelectedAndLoadedGroup({artist_name: (groupIdentifier as ArtistIdentifierAPI).artist_name, ...baseErrorState,} as ReleaseGroupArtist);
            } else if (groupBy === "week") {
                setSelectedAndLoadedGroup({
                    week_range_mon_sun: (groupIdentifier as WeekIdentifierAPI).week_range_mon_sun,
                    week_start_date: (groupIdentifier as WeekIdentifierAPI).week_start_date, ...baseErrorState,
                } as ReleaseGroupWeek);
            } else {
                setSelectedAndLoadedGroup(null);
            }
        } finally {
            setLoadingSelectedGroupReleases(false);
        }
    };

    useEffect(() => {
        const fetchArchiveStatuses = async () => {
            const statuses: { [key: string]: boolean } = {};
            for (const record of releases.data || []) {
                try {
                    const response = await axiosApiInstance.get(
                        `/api/products/releases/${record.id}/archivestatus`,
                        {
                            responseType: "json"
                        }
                    );
                    statuses[record.upc_ean] = response.data.isArchived; // Still using UPC as key for backward compatibility
                } catch (error) {
                    console.error(`Error fetching archive status for ${record.id}:`, error);
                    statuses[record.upc_ean] = false
                }
            }
            setArchiveStatuses(statuses);
        };

        if (releases.data) {
            fetchArchiveStatuses();
        }
    }, [releases.data]);

    useEffect(() => {
        // console.log('upc to platform map: ', upcToPlatformMap);
        // console.log('upc to formref map: ', upcToFormRefMap);
        // console.log('selected totals: ', selectedTotals);
    }, [upcToFormRefMap, upcToPlatformMap, selectedTotals])

    // useEffect(() => {
    //     console.log(selectedTotals);
    //     console.log('youtube selected totals === equality ', selectedTotals.youtube === selectedTotals.total);
    //     console.log(
    //         'youtube selected totals double == equality',
    //         selectedTotals.youtube == selectedTotals.total
    //     );
    // }, [selectedTotals]);

    // const [selected, setSelected] = useState<Array<any>>([]);
    const navigate = useNavigate()
    const location = useLocation()

    const expandedRowRender = (record: API.MusicDistribution.MusicRelease) => {
        const columns: TableColumnsType<API.MusicDistribution.ArtTrack> = [
            // {title: 'Track #', key: '#', render: (_dom, _entity, index) => <Badge count={index + 1}/>},
            {
                title: "Vol. #",
                key: "vol_no",
                render: (_dom, entity) => entity.volume_number,
            },
            {
                title: "Track #",
                key: "track_no",
                render: (_dom, entity, _index) => entity.track_number,
            },
            {title: "Title", dataIndex: "title", key: "title"},
            {title: "ISRC", dataIndex: "isrc", key: "isrc"},
            // {title: 'Duration', dataIndex: 'duration', key: 'duration'},
            {
                title: "Artist",
                dataIndex: "track_artist",
                key: "track_artist",
                render: (_text, {main_artists}) =>
                    // track_artist.names.find(({ is_default }) => is_default).full_name
                    main_artists.map((artist) => artist.key_name).join(", "),
            },
            {
                title: "Featuring Artists",
                dataIndex: "featuring_artists",
                key: "featuring_artists",
                render: (_text, {featuring_artists}) =>
                    featuring_artists.map((artist) => artist.key_name).join(", "),
                // featuring_artists
                //     ?.map(({ names }) => names.find(({ is_default }) => is_default).full_name)
                //     .join(', ')
            },
            {
                title: "Filename",
                dataIndex: "filename",
                key: "filename",
                render: (text) => <i>{text}</i>,
            },
            {
                title: "",
                render: (_dom, track) => {
                    return (
                        track.is_uploaded && (
                            <Tooltip title="Download">
                                <Button
                                    type="link"
                                    icon={<DownloadOutlined/>}
                                    onClick={async () => {
                                        try {
                                            const response = await axiosApiInstance.post(
                                                "/api/products/releases/generate-presigned-url-get",
                                                {
                                                    fileName: track.filename,
                                                    folderName: record.id,
                                                }
                                            )

                                            const {presignedUrl} = response.data

                                            window.open(presignedUrl, "_blank")
                                        } catch (err) {
                                            console.error("Error downloading file:", err)
                                            message.error("Error downloading file")
                                        }
                                    }}
                                />
                            </Tooltip>
                        )
                    )
                },
            },
        ]

        const data = record.art_tracks ?? []
        return (
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                rowKey={"isrc"}
            />
        )
    }

    // const filterDropdown = ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
    //     <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
    //         <Input
    //             placeholder={`Search Invoice`}
    //             value={selectedKeys[0]}
    //             onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
    //             onPressEnter={() => confirm()}
    //             style={{marginBottom: 8, display: 'block'}}
    //         />
    //         <Space>
    //             <Button
    //                 type='primary'
    //                 onClick={() => {
    //                     confirm();
    //                 }}
    //                 icon={<SearchOutlined/>}
    //                 size='small'
    //                 style={{width: 90}}>
    //                 Search
    //             </Button>
    //             <Button
    //                 onClick={() => {
    //                     clearFilters && clearFilters(); // re ti kanoume
    //                     setSelectedKeys([]);
    //                     confirm();
    //                     close();
    //                 }}
    //                 size='small'
    //                 style={{width: 90}}>
    //                 Clear
    //             </Button>
    //         </Space>
    //     </div>
    // );
    // const filterIcon = (filtered: boolean) => (
    //     <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
    // );

    const selectAllFunc = (platform: string, checked: boolean) => {
        // console.log({upcToFormRefMap, upcToPlatformMap})
        // for (const id in upcToFormRefMap) {
        for (const upc in upcToFormRefMap) {
            const new_list =
                upcToPlatformMap[upc]?.filter((p) => p !== platform) || []


            if (checked) {
                new_list.push(platform)
            }

            upcToFormRefMap[upc]?.setFieldValue(`${upc}-checkbox-group`, new_list)

            setUpcToPlatformMap((m) => {
                m[upc] = new_list
                return m
            })
        }
        setSelectedTotals((prev) => {
            const newTotals = {...prev}
            // Count how many UPCs have this platform selected after the update
            const count = Object.values(upcToPlatformMap).filter((platforms) =>
                platforms.includes(platform)
            ).length

            // Update the platform count based on actual selections
            newTotals[platform] = checked ? count : 0
            // Update total based on all platforms
            newTotals.total = ["youtube", "spotify", "believe"].reduce(
                (sum, platform) => sum + (newTotals[platform] || 0),
                0
            )
            return newTotals
        })
    }

    const columns: ProColumns<API.MusicDistribution.MusicRelease>[] = [
        {
            title: (
                <Space
                    direction="vertical"
                    style={{width: 60}}
                >
                    <Row style={{width: 60}}>
                        {[
                            <FontAwesomeIcon icon={faYoutube}/>,
                            <FontAwesomeIcon
                                icon={faB}
                                style={{marginLeft: 4}}
                            />,
                            <FontAwesomeIcon
                                icon={faSpotify}
                                style={{marginLeft: 1}}
                            />,
                        ].map((icon) => (
                            <Col
                                span={8}
                                style={CHECKBOX_COL_STYLE}
                            >
                                {icon}
                            </Col>
                        ))}
                    </Row>
                    <CheckAllCheckbox selectAllFunc={selectAllFunc}/>
                </Space>
            ),
            key: "upload_checks",
            search: false,
            render: (_dom, entity) => <UploaderCheckbox entity={entity}/>,
        },
        {
            title: "",
            key: "image",
            search: false,
            render: (_dom, entity) => (
                <AlbumCover
                    upc={entity.upc_ean}
                    filename={entity.album_cover_filename}
                    id={entity.id}
                />
            ),
        },
        {
            title: "Album Name",
            dataIndex: "name",
            key: "name",
            sorter: true,
            sortOrder:
                searchCriteria.sorter_field === "name"
                    ? (searchCriteria.sorter_order as SortOrder)
                    : null,
            render(_dom, entity, _index, _action, _schema) {
                let name = entity.name

                if (entity.subtitle != "") {
                    name = `${entity.name} (${entity.subtitle})`
                }
                return (
                    <Link
                        to={`/music/${entity.id}/view`}
                        state={{background: location}}
                    >
                        {name}
                    </Link>
                )
            },
        },
        {
            title: "UPC",
            dataIndex: "upc_ean",
            key: "upc",
            sorter: true,
            sortOrder:
                searchCriteria.sorter_field === "upc_ean"
                    ? (searchCriteria.sorter_order as SortOrder)
                    : null,
        },
        {
            title: "Artist",
            dataIndex: "main_artist",
            key: "main_artist",
            render: (_text, record: API.MusicDistribution.MusicRelease) =>
                record.artist_display?.trim() ? record.artist_display : (record.artists?.map((artist) => artist.key_name).join(", ") || 'N/A'),
        },
        {
            title: "Label",
            dataIndex: "label",
            key: "label",
            render: (_text, record: API.MusicDistribution.MusicRelease) => record.label?.name || 'N/A',
        },
        {
            title: "Asset Label",
            dataIndex: "asset_label",
            key: "asset_label",
            render: (_text, record: API.MusicDistribution.MusicRelease) => record.asset_label?.name || 'N/A',
        },
        {
            title: "Podio ID",
            dataIndex: "podio_id",
            key: "podio_id",
            width: 110,
        },
        {
            title: "Format",
            dataIndex: "type",
            key: "type",
            sorter: true,
            sortOrder:
                searchCriteria.sorter_field === "type"
                    ? (searchCriteria.sorter_order as SortOrder)
                    : null,
        },
        {
            title: "Release Date",
            dataIndex: "digital_release_date",
            key: "digital_release_date",
            valueType: "date",
            sorter: true,
            sortOrder:
                searchCriteria.sorter_field === "digital_release_date"
                    ? (searchCriteria.sorter_order as SortOrder)
                    : null,
        },
        {
            title: "Upload Status",
            key: "upload_status",
            hideInTable: releasesView !== "verified",
            children: [
                {
                    title: "YouTube",
                    width: 100,
                    align: "center",
                    key: "youtube_status",
                    render: (_text, record) => renderPlatformDeliveryCell(record, "youtube"),
                },
                {
                    title: "Believe",
                    width: 100,
                    align: "center",
                    key: "believe_status",
                    render: (_text, record) => renderPlatformDeliveryCell(record, "believe"),
                },
                {
                    title: "Spotify",
                    width: 100,
                    align: "center",
                    key: "spotify_status",
                    render: (_text, record) => renderPlatformDeliveryCell(record, "spotify"),
                },
            ],
        },
        {
            title: "Archived",
            width: 100,
            align: "center",
            key: "archive_status",
            render: (_text, record) => {
                const isArchived = archiveStatuses[record.upc_ean];

                if (isArchived === null) {
                    return <Spin/>;
                }

                return isArchived ? (
                    <Tooltip title="Archived">
                        {ArchiveStatusBadgeMap["archived"]}
                    </Tooltip>
                ) : (
                    <Tooltip title="Unarchived">
                        {ArchiveStatusBadgeMap["unarchived"]}
                    </Tooltip>
                )
            },
        },


        {
            title: "",
            render: (_dom, record) => {
                return (
                    <>
                        {
                            <Row>
                                <Tooltip title="Edit">
                                    <Button
                                        type="link"
                                        icon={<EditOutlined/>}
                                        onClick={() =>
                                            navigate(`/music/create/${record.id}`)
                                        }
                                    />
                                </Tooltip>
                                <Tooltip title="Archive">
                                    <Button
                                        key={`${record.upc_ean}`}
                                        type="link"
                                        icon={<ClockCircleOutlined/>}
                                        onClick={async () => {
                                            message.loading(`Archiving Release : ${record.name}`)
                                            try {
                                                await axiosApiInstance.get(
                                                    `/api/digitalassets/archive/${record.id}`
                                                )

                                                releasesActionRef.current?.reload()

                                                await axiosApiInstance.post(
                                                    `/api/digitalassets/bucketconvert/${record.id}`
                                                )
                                                releasesActionRef.current?.reload()

                                                await axiosApiInstance.put(
                                                    `/api/products/releases/${record.id}/archivestatus`
                                                )

                                                releasesActionRef.current?.reload()

                                                message.success("Release Archived successfully!", 3)
                                            } catch (error) {
                                                console.error(error)
                                                message.error(
                                                    "Failed to Archive release. Please try again."
                                                )
                                            }
                                        }}
                                    />
                                </Tooltip>
                                {/* <Tooltip title="Unverify">
                  <Button
                    type="link"
                    icon={<StopOutlined />}
                    onClick={async () => {
                      await axiosApiInstance
                        .put(
                          `/api/products/releases/${record.id}/unverify`
                        )
                        .catch((reason) => {
                          console.error(reason)
                        })
                      releasesActionRef.current?.reload()
                    }}
                  />
                </Tooltip> */}
                                <DownloadBinariesModal
                                    onClick={async () => {
                                        const data = await axiosApiInstance
                                            .get(
                                                `/api/products/releases/${record.id}/binaries`,
                                                {
                                                    responseType: "arraybuffer",
                                                }
                                            )
                                            .then(({data}) => data)

                                        const url = window.URL.createObjectURL(new Blob([data]))
                                        const link = document.createElement("a")
                                        link.href = url
                                        link.setAttribute("download", `${record.id}.zip`)
                                        document.body.appendChild(link)
                                        // loadingmsg();
                                        link.click()
                                        message.success("Files Downloaded successfully!", 3)
                                    }}
                                />

                                <UploadRelease
                                    release={record}
                                    upc_ean={record.upc_ean}
                                    id={record.id}
                                    key={`${record.id}-upload-release-checkbox-group`}
                                />
                                <ModifyRelease
                                    release={record}
                                    upc_ean={record.upc_ean}
                                    id={record.id}
                                    key={`${record.id}-modify-release-checkbox-group`}
                                />
                                <TakedownRelease
                                    release={record}
                                    upc_ean={record.upc_ean}
                                    id={record.id}
                                    key={`${record.id}-delete-release-checkbox-group`}
                                />
                            </Row>
                        }
                    </>
                );
            }
        },
    ]

    const minicolumns: TableColumnsType<{ upc: string }> = [
        {
            title: "UPC",
            dataIndex: "upc",
            key: "upc",
        },
        {
            title: "Title",
            // dataIndex: 'name',
            key: "title",
            render(_dom, record) {
                return upcToNameMap[record.upc]
            },
        },
        {
            title: <FontAwesomeIcon icon={faYoutube}/>,

            render(_dom, record) {
                return upcToPlatformMap[record.upc].includes("youtube") ? (
                    <Badge color="blue"/>
                ) : (
                    <></>
                )
            },
        },
        {
            title: (
                <FontAwesomeIcon
                    icon={faB}
                    style={{marginLeft: 4}}
                />
            ),
            render(_dom, record) {
                return upcToPlatformMap[record.upc].includes("believe") ? (
                    <Badge color="blue"/>
                ) : (
                    <></>
                )
            },
        },
        {
            title: (
                <FontAwesomeIcon
                    icon={faSpotify}
                    style={{marginLeft: 1}}
                />
            ),
            render(_dom, record) {
                return upcToPlatformMap[record.upc].includes("spotify") ? (
                    <Badge color="blue"/>
                ) : (
                    <></>
                )
            },
        },
    ]

    const proTableProps = {
        columns,
        rowKey: "upc_ean",
        search: false,
        columnsState: {
            defaultValue: {
                archive_status: {show: false},
            },
        },
        expandable: {expandedRowRender},
        actionRef: releasesActionRef,
        toolBarRender: () => [
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                    navigate("/music/create")
                }
            >
                Add
            </Button>,
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                    navigate("/music/batch")
                }
                style={{ marginLeft: 8 }}
            >
                Batch Process
            </Button>,
            <Button
                type="primary"
                icon={<UploadOutlined/>}
                disabled={selectedTotals.total === 0}
                onClick={() => {
                    const nameMap: { [k: string]: string } = {};
                    (groupBy === "none" ? releases?.data : selectedGroup?.releases || []).forEach((r) => {
                        nameMap[r.upc_ean] = r.name
                    })
                    setUpcToNameMap(nameMap)
                    setIsOpen(true)
                }}
            >
                Upload
            </Button>,
            <Modal
                title="Upload Releases"
                open={isOpen}
                destroyOnClose
                onOk={async () => {
                    const upcs = getSelectedUPCs(upcToPlatformMap)
                    const allReleases: API.MusicDistribution.MusicRelease[] =
                        (groupBy === "none" ? releases?.data : selectedGroup?.releases) || []
                    const byUpc = Object.fromEntries(allReleases.map((r) => [r.upc_ean, r]))
                    let scheduled = 0
                    let skipped = 0
                    try {
                        for (const upc of upcs) {
                            const record = byUpc[upc]
                            const wanted = (upcToPlatformMap[upc] || []).map(String)
                            const uploadTo = wanted.filter((platform) => {
                                if (
                                    originalUploadBlockedStatus(
                                        latestDeliveryForPlatform(record, platform)?.status
                                    )
                                ) {
                                    skipped += 1
                                    return false
                                }
                                return true
                            })
                            if (uploadTo.length === 0) {
                                continue
                            }
                            axiosApiInstance.put(
                                `/api/products/releases/${upc}/upload`,
                                undefined,
                                {params: {upload_to: uploadTo}}
                            )
                            scheduled += 1
                        }
                        if (scheduled === 0) {
                            notification.warning({
                                message: 'Nothing to upload',
                                description:
                                    'Selected platforms are already ingested or still ingesting. Use Modify for metadata, or wait.',
                            })
                            return
                        }
                        notification.success({
                            message: skipped
                                ? `${scheduled} upload(s) scheduled, ${skipped} already-live platform(s) skipped`
                                : `${scheduled} upload(s) scheduled`,
                        })
                        setIsOpen(false)
                    } catch (err) {
                        notification.error({message: "Error scheduling uploads"})
                        setIsOpen(false)
                    }
                }}
                onCancel={() => setIsOpen(false)}
                width={800}
            >
                <p>Release Details</p>
                <br/>
                <Table
                    size="small"
                    columns={minicolumns}
                    dataSource={getSelectedUPCs(upcToPlatformMap).map((upc) => ({
                        upc,
                    }))}
                />
            </Modal>,
            <ExportReleasesExcel/>,
            <ExportTemplateExcel/>,
            // <ImportUpdateReleasesExcel/>,
        ],
        toolbar: {
            menu: {
                activeKey: releasesView,
                items: [
                    {
                        key: "verified",
                        label: "",
                    },
                ],
            },
        },
    };

    return (
        <>
            <div style={{marginBottom: 16, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8}}>
                <span>View:</span>
                <Radio.Group
                    value={layout}
                    optionType="button"
                    buttonStyle="solid"
                    options={[
                        {label: "Table", value: "table"},
                        {label: "Folders", value: "folders"},
                    ]}
                    onChange={(event) => {
                        const next = event.target.value as "table" | "folders";
                        setOpenFolder(null);
                        if (next === "folders") {
                            tablePageSizeRef.current = searchCriteria.pageSize || 10;
                            setSearchCriteria((prev) => ({...prev, page: 1, pageSize: FOLDER_VIEW_PAGE_SIZE}));
                            if (activeGroupIdentifier) {
                                fetchReleasesForSelectedGroup(activeGroupIdentifier, 1, FOLDER_VIEW_PAGE_SIZE, true);
                            }
                        } else {
                            setSearchCriteria((prev) => ({
                                ...prev,
                                page: 1,
                                pageSize: tablePageSizeRef.current || 10,
                            }));
                            if (activeGroupIdentifier) {
                                fetchReleasesForSelectedGroup(activeGroupIdentifier, 1, FOLDER_PER_PAGE, false);
                            }
                        }
                        setLayout(next);
                    }}
                />
                <span style={{marginLeft: 8}}>Group by:</span>
                <Select
                    value={groupBy}
                    options={GROUP_OPTIONS}
                    style={{width: 180}}
                    onChange={(value) => {
                        setOpenFolder(null);
                        setGroupBy(value);
                        setCurrentGroupIdentifiersPage(1);
                        setSelectedAndLoadedGroup(null);
                        setTotalGroupIdentifiersState(0);
                        setActiveGroupIdentifier(null);
                        setGroupIdentifiers([]);
                        setTotalGroupIdentifiers(0);
                    }}
                />
                {groupBy === "week" && (
                    <>
                        <span style={{marginRight: 8, marginLeft: 16}}>Order:</span>
                        <Select
                            value={order}
                            options={ORDER_OPTIONS}
                            style={{width: 150}}
                            onChange={(value) => {
                                setOrder(value);
                                setCurrentGroupIdentifiersPage(1);
                                setSelectedAndLoadedGroup(null);
                                setTotalGroupIdentifiersState(0);
                                setActiveGroupIdentifier(null);
                            }}
                        />
                    </>
                )}
            </div>

            {groupBy === "none" ? (
                <ProTable
                    {...proTableProps}
                    dataSource={releasesForNoneView.data}
                    loading={releasesForNoneView.isFetching}
                    search={false}
                    tableRender={layout === "folders" ? (_props, _dom, {toolbar}) => (
                        <>
                            {toolbar}
                            {openFolder ? (
                                <ReleaseFolderFiles release={openFolder} onBack={() => setOpenFolder(null)}/>
                            ) : (
                                <ReleaseFolderGrid
                                    releases={releasesForNoneView.data ?? []}
                                    loading={releasesForNoneView.isFetching}
                                    onOpen={setOpenFolder}
                                />
                            )}
                            {!openFolder && (
                            <div style={{display: "flex", justifyContent: "flex-end", padding: 16}}>
                                <Pagination
                                    pageSize={searchCriteria.pageSize || FOLDER_VIEW_PAGE_SIZE}
                                    current={searchCriteria.page}
                                    total={totalReleasesForNoneView}
                                    onChange={(page, pageSize) => {
                                        setSearchCriteria((prev) => ({...prev, page, pageSize}))
                                    }}
                                    hideOnSinglePage
                                    showSizeChanger
                                />
                            </div>
                            )}
                        </>
                    ) : undefined}
                    pagination={{
                        pageSize: searchCriteria.pageSize,
                        current: searchCriteria.page,
                        total: totalReleasesForNoneView,
                        onChange: (page, pageSize) => {
                            setSearchCriteria((prev) => ({...prev, page, pageSize}))
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: true,
                    }}
                    headerTitle={releasesView === "verified" ? <ProCard title="Verified Releases"/> :
                        <ProCard title="Pending Releases"/>}
                    onChange={({current, pageSize}, _, sorter: any) => {
                        setSearchCriteria((prev) => ({
                            ...prev,
                            page: current ?? 1,
                            pageSize: pageSize ?? 10,
                            sorter_field: Array.isArray(sorter)
                                ? (typeof sorter[0]?.field === "string" ? sorter[0]?.field : undefined)
                                : (typeof sorter?.field === "string" ? sorter?.field : undefined),
                            sorter_order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
                        }));
                    }}
                />
            ) : (
                <Spin
                    spinning={loadingGroupIdentifiers || (activeGroupIdentifier && selectedAndLoadedGroup && selectedAndLoadedGroup.releasesLoading)}>

                    {!activeGroupIdentifier && (
                        <>
                            {!loadingGroupIdentifiers && (
                                <>
                                    <div style={{marginBottom: 16}}>
                                        {groupIdentifiers.map((group, idx) => {
                                            const isArtistGroup = groupBy === "artist";
                                            const groupLabel = isArtistGroup
                                                ? (group as ArtistIdentifierAPI).artist_name
                                                : (group as WeekIdentifierAPI).week_range_mon_sun;
                                            const numberOfReleases = (group as ArtistIdentifierAPI | WeekIdentifierAPI).number_of_releases;

                                            return (
                                                <Button
                                                    key={`${groupLabel}-${idx}`}
                                                    type="default"
                                                    block
                                                    onClick={() => handleGroupIdentifierClick(group)}
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '4px',
                                                        textAlign: 'left',
                                                        paddingLeft: '12px',
                                                        paddingRight: '12px',
                                                    }}
                                                >
                                                    <span style={{flexGrow: 1}}>{groupLabel}</span>
                                                    <Badge
                                                        count={numberOfReleases}
                                                        color="gray"
                                                    />
                                                    <ArrowRightOutlined
                                                        style={{
                                                            fontSize: '12px',
                                                            color: '#888',
                                                            marginTop: '4px',
                                                        }}
                                                    />
                                                </Button>
                                            );
                                        })}
                                    </div>

                                    {groupIdentifiers.length > 0 && totalGroupIdentifiersState > FOLDER_PER_PAGE && (
                                        <Pagination
                                            current={currentGroupIdentifiersPage}
                                            pageSize={FOLDER_PER_PAGE}
                                            total={totalGroupIdentifiersState}
                                            onChange={(page) => {
                                                setCurrentGroupIdentifiersPage(page);
                                                setSelectedAndLoadedGroup(null);
                                                setTotalGroupIdentifiersState(0);
                                            }}
                                            style={{marginBottom: 16, textAlign: "center"}}
                                            showSizeChanger={false}
                                            hideOnSinglePage={true}
                                        />
                                    )}
                                    {groupIdentifiers.length === 0 && (
                                        <div style={{textAlign: 'center', padding: '20px', color: '#aaa'}}>
                                            No {groupBy === 'artist' ? 'artists' : 'items'} found.
                                        </div>
                                    )}
                                </>
                            )}
                            {loadingGroupIdentifiers && (
                                <div style={{textAlign: 'center', padding: '50px'}}>
                                    <Spin size="large"
                                          tip={`Loading ${groupBy === 'artist' ? 'artists' : 'weeks'}...`}/>
                                </div>
                            )}
                        </>
                    )}

                    {activeGroupIdentifier && (
                        <>
                            {activeGroupIdentifier && selectedAndLoadedGroup && !selectedAndLoadedGroup.releasesLoading && (
                                <>
                                    <Button
                                        onClick={handleBackToGroups}
                                        style={{marginBottom: 16}}
                                        icon={<LeftOutlined/>}
                                    >
                                        Back to {groupBy === 'artist' ? 'Artists' : 'Weeks'} List
                                    </Button>
                                    <ProTable
                                        {...proTableProps}
                                        dataSource={selectedAndLoadedGroup.releases}
                                        loading={false}
                                        search={false}
                                        tableRender={layout === "folders" ? (_props, _dom, {toolbar}) => (
                                            <>
                                                {toolbar}
                                                {openFolder ? (
                                                    <ReleaseFolderFiles release={openFolder} onBack={() => setOpenFolder(null)}/>
                                                ) : (
                                                    <ReleaseFolderGrid
                                                        releases={selectedAndLoadedGroup.releases ?? []}
                                                        loading={false}
                                                        onOpen={setOpenFolder}
                                                    />
                                                )}
                                                {!openFolder && (
                                                <div style={{display: "flex", justifyContent: "flex-end", padding: 16}}>
                                                    <Pagination
                                                        current={selectedAndLoadedGroup.releasesCurrentPage}
                                                        pageSize={selectedAndLoadedGroup.releasesPageSize || FOLDER_VIEW_PAGE_SIZE}
                                                        total={totalReleasesForSelectedGroupAtom}
                                                        onChange={(page, pageSize) => {
                                                            if (activeGroupIdentifier) {
                                                                fetchReleasesForSelectedGroup(activeGroupIdentifier, page, pageSize);
                                                            }
                                                        }}
                                                        hideOnSinglePage
                                                        showSizeChanger
                                                    />
                                                </div>
                                                )}
                                            </>
                                        ) : undefined}
                                        headerTitle={
                                            groupBy === "artist"
                                                ? `Releases by: ${(selectedAndLoadedGroup as ReleaseGroupArtist).artist_name}`
                                                : `Releases in week: ${(selectedAndLoadedGroup as ReleaseGroupWeek).week_range_mon_sun}`
                                        }
                                        pagination={{
                                            current: selectedAndLoadedGroup.releasesCurrentPage,
                                            pageSize: selectedAndLoadedGroup.releasesPageSize || FOLDER_PER_PAGE,
                                            total: totalReleasesForSelectedGroupAtom,
                                            onChange: (page, pageSize) => {
                                                if (activeGroupIdentifier) {
                                                    fetchReleasesForSelectedGroup(activeGroupIdentifier, page, pageSize);
                                                } else {
                                                    console.error("Pagination change for selected group, but activeGroupIdentifier is null.");
                                                }
                                            },
                                            hideOnSinglePage: true,
                                            showSizeChanger: true,
                                        }}
                                    />
                                </>
                            )}
                            {(!selectedAndLoadedGroup || selectedAndLoadedGroup.releasesLoading) && (
                                <div style={{textAlign: 'center', padding: '50px'}}>
                                    <Spin
                                        size="large"
                                        tip={`Loading releases for ${
                                            activeGroupIdentifier ?
                                                (groupBy === 'artist' ? (activeGroupIdentifier as ArtistIdentifierAPI)?.artist_name : (activeGroupIdentifier as WeekIdentifierAPI)?.week_range_mon_sun)
                                                : 'selected group'
                                        }...`}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Spin>
            )}
        </>
    );
};

export default ReleasesTable;
